import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  FormGroup,
  FormControlLabel,
  Checkbox,
  useTheme,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  Grid
} from '@mui/material';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import Favicon from 'components/Favicon';
import { ChevronRight } from '@mui/icons-material';
import { RadarChartOutlined } from '@ant-design/icons';
import GridViewIcon from '@mui/icons-material/GridView';

const CompetitiveIndex = () => {
  const theme = useTheme();

  // Get data from Redux
  const sbsData = useSelector((state) => state.competitorsReducer.competitorsAnalysisResults?.side_by_side_analysis);

  // Access the analysis object
  const analysis = sbsData?.analysis || {};

  // Get the company names
  const companies = Object.keys(analysis['5_company_scoring'] || {});

  // State to track which companies are selected for display
  const [selectedCompanies, setSelectedCompanies] = useState(companies.reduce((acc, company) => ({ ...acc, [company]: true }), {}));

  // State for view selection
  const [view, setView] = useState('individual');

  // Get parameters for radar chart (excluding total)
  const parameters = companies.length > 0 ? Object.keys(analysis['5_company_scoring'][companies[0]] || {}) : [];

  // Get scoring parameters
  const scoringParameters = analysis['3_scoring_parameters'] || [];

  // Format parameter names for display
  const formatParameterName = (param) => {
    return param
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Transform data for radar chart
  const transformDataForRadarChart = () => {
    return parameters.map((param) => {
      const dataPoint = {
        parameter: formatParameterName(param)
      };

      companies.forEach((company) => {
        if (selectedCompanies[company]) {
          dataPoint[company] = analysis['5_company_scoring'][company]?.[param] || 0;
        }
      });

      return dataPoint;
    });
  };

  // Transform data for individual company radar charts
  const transformDataForIndividualRadar = (company) => {
    return parameters.map((param) => ({
      parameter: formatParameterName(param),
      value: analysis['5_company_scoring'][company]?.[param] || 0
    }));
  };

  // Handle checkbox change for company selection
  const handleCompanyToggle = (company) => {
    setSelectedCompanies({
      ...selectedCompanies,
      [company]: !selectedCompanies[company]
    });
  };

  // Handle view change
  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setView(newView);
    }
  };

  // Get a color for each company
  const getCompanyColor = (index) => {
    const colors = [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.success.main,
      theme.palette.warning.main,
      theme.palette.error.main,
      theme.palette.info.main,
      '#8884d8',
      '#82ca9d',
      '#ffc658',
      '#ff8042'
    ];
    return colors[index % colors.length];
  };

  // Count how many companies are selected
  const selectedCount = Object.values(selectedCompanies).filter(Boolean).length;

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // Find the scoring parameter for this category
      const scoringParam = scoringParameters.find((param) => param.id === label.toLowerCase().replace(/\s+/g, '_'));

      return (
        <Paper elevation={3} sx={{ p: 2, minWidth: 300 }}>
          <Typography variant="subtitle1" gutterBottom>
            {label}
          </Typography>
          <Divider sx={{ my: 1 }} />
          {payload.map((entry, index) => (
            <Box key={`item-${index}`} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  bgcolor: entry.color,
                  mr: 1,
                  borderRadius: '50%'
                }}
              />
              <Typography variant="body2" sx={{ mr: 'auto' }}>
                {entry.name}:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {entry.value}/5
              </Typography>
            </Box>
          ))}
          {scoringParam && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Scoring Criteria:
              </Typography>
              <Box sx={{ pl: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  1: {scoringParam['1']}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  3: {scoringParam['3']}
                </Typography>
                <Typography variant="body2" color="success.main">
                  5: {scoringParam['5']}
                </Typography>
              </Box>
            </>
          )}
        </Paper>
      );
    }
    return null;
  };

  // Render individual radar charts
  const renderIndividualRadars = () => {
    const selectedCompaniesList = companies.filter((company) => selectedCompanies[company]);

    return (
      <Grid container spacing={3}>
        {selectedCompaniesList.map((company, index) => (
          <Grid item xs={12} md={6} key={company}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3,
                    mt: 1
                  }}
                >
                  <Favicon company={company} size={36} />
                  <Typography
                    variant="h5"
                    sx={{
                      ml: 1,
                      textAlign: 'center'
                    }}
                  >
                    {company}
                  </Typography>
                </Box>
                <Box sx={{ height: 400 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart cx="50%" cy="50%" outerRadius="80%" data={transformDataForIndividualRadar(company)}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="parameter" />
                      <PolarRadiusAxis angle={30} domain={[0, 5]} />
                      <Tooltip content={<CustomTooltip />} />
                      <Radar
                        name={company}
                        dataKey="value"
                        stroke={getCompanyColor(index)}
                        fill={getCompanyColor(index)}
                        fillOpacity={0.2}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h4" gutterBottom>
              Competitive Index - Radar Analysis
            </Typography>
            <ToggleButtonGroup value={view} exclusive onChange={handleViewChange} size="small">
              <ToggleButton value="individual" aria-label="individual view">
                <GridViewIcon />
              </ToggleButton>
              <ToggleButton value="combined" aria-label="combined view">
                <RadarChartOutlined />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Compare parameters across selected companies
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Box
            sx={{
              mb: 2,
              p: 2,
              bgcolor: 'rgba(0, 118, 255, 0.05)',
              borderRadius: 1,
              borderLeft: '4px solid',
              borderColor: 'primary.main'
            }}
          >
            <Typography component="span" variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main', display: 'block', mb: 1 }}>
              <strong>💡 How to use this chart?</strong>
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
              <ChevronRight sx={{ color: 'primary.main', fontSize: 20, mt: 0.3 }} />
              <Typography variant="body1">
                <strong>Compare competitors at a glance:</strong> See how each company performs across all key parameters on a single chart.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <ChevronRight sx={{ color: 'primary.main', fontSize: 20, mt: 0.3 }} />
              <Typography variant="body1">
                <strong>Spot strengths and gaps:</strong> Quickly identify where your company leads, where you lag, and where the market
                leaders are.
              </Typography>
            </Box>
          </Box>

          {/* Company Selection Checkboxes */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 3 }}>
            <FormGroup row>
              {companies.map((company, index) => (
                <FormControlLabel
                  key={company}
                  control={
                    <Checkbox
                      checked={selectedCompanies[company]}
                      onChange={() => handleCompanyToggle(company)}
                      name={company}
                      sx={{
                        color: getCompanyColor(index),
                        '&.Mui-checked': {
                          color: getCompanyColor(index)
                        }
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Favicon company={company} size={16} />
                      {company}
                    </Box>
                  }
                />
              ))}
            </FormGroup>
          </Box>

          {/* Radar Chart(s) */}
          {selectedCount > 0 ? (
            view === 'combined' ? (
              <Box sx={{ width: '100%', height: 500 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart cx="50%" cy="50%" outerRadius="80%" data={transformDataForRadarChart()}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="parameter" />
                    <PolarRadiusAxis angle={30} domain={[0, 5]} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />

                    {companies.map(
                      (company, index) =>
                        selectedCompanies[company] && (
                          <Radar
                            key={company}
                            name={company}
                            dataKey={company}
                            stroke={getCompanyColor(index)}
                            fill={getCompanyColor(index)}
                            fillOpacity={0.2}
                          />
                        )
                    )}
                  </RadarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              renderIndividualRadars()
            )
          ) : (
            <Typography variant="subtitle1" align="center" sx={{ py: 10 }}>
              Please select at least one company to display data
            </Typography>
          )}

          {/* Explanation */}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            The radar chart displays company scores across multiple parameters on a scale of 1-5. Higher scores indicate better performance
            in each category.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CompetitiveIndex;
